import {Component, OnInit, Input, Renderer2} from '@angular/core';
import * as d3 from 'd3';
import { formatNumber } from '@angular/common';
import {MessageService} from 'primeng/api';
import {authInterceptorProviders} from '../_helpers/auth.interceptor';
import {Globals} from '../globals';

@Component({
  selector: 'app-d3matrix',
  templateUrl: './d3matrix.component.html',
  styleUrls: ['./d3matrix.component.css']
})
export class D3matrixComponent implements OnInit {
  @Input() params: string = '';
  @Input() token: string = '';

  loading: boolean = false;
  data = [];
  svg: any;

  valueType = 'PAX';
  fillAllCells = true;
  figureHeight = '700px';
  // colorScheme = "YlGn";

  constructor(private messageService: MessageService, private globals: Globals) { }

  ngOnInit(): void {
    this.showD3(this.params, true);
  }

  // Used in the legend function to show colors
  ramp(base: string, color: any, n = 256): any {
    const canvas = d3.select('#'+base).append("canvas")
      .attr("width", n)
      .attr("height", 1)
      .attr("style","display:none")
      .node();
    if(canvas != null) {
      const context = canvas.getContext("2d");
      if (context != null) {
        for (let i = 0; i < n; ++i) {
          context.fillStyle = color(i / (n - 1));
          context.fillRect(i, 0, 1, 1);
        }
      }
    }
    return canvas;
  }

  // Generate the color legend
  legend(base: string, color: any, title: string) {
    let me = this;
    let tickSize = 6,
      width = 320,
      height = 44 + tickSize,
      marginTop = 18,
      marginRight = 0,
      marginBottom = 16 + tickSize,
      marginLeft = 50,
      ticks = width / 64,
      tickFormat: any,
      tickValues: any;
    d3.select('#'+base).selectAll("*").remove();
    const svg = d3.select('#'+base).append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", '0 0 ' + width.toString() + ' ' + height.toString())
      .style("overflow", "visible")
      .style("display", "block");

    let x: any;

    // Continuous
    if (color.interpolator) {
      x = Object.assign(color.copy()
          .interpolator(d3.interpolateRound(marginLeft, width - marginRight)),
        {range() { return [marginLeft, width - marginRight]; }});

      svg.append("image")
        .attr("x", marginLeft)
        .attr("y", marginTop)
        .attr("width", width - marginLeft - marginRight)
        .attr("height", height - marginTop - marginBottom)
        .attr("preserveAspectRatio", "none")
        .attr("xlink:href", me.ramp(base, color.interpolator()).toDataURL());

      // scaleSequentialQuantile doesn’t implement ticks or tickFormat.
      if (!x.ticks) {
        if (tickValues === undefined) {
          const n = Math.round(ticks + 1);
          tickValues = d3.range(n).map(i => d3.quantile(color.domain(), i / (n - 1)));
        }
        if (typeof tickFormat !== "function") {
          tickFormat = d3.format(tickFormat === undefined ? ",f" : tickFormat);
        }
      }
    }

    // Discrete
    else if (color.invertExtent) {
      const thresholds
        = color.thresholds ? color.thresholds() // scaleQuantize
        : color.quantiles ? color.quantiles() // scaleQuantile
          : color.domain(); // scaleThreshold

      const thresholdFormat
        = tickFormat === undefined ? (d: any) => d :
            typeof tickFormat === "string" ? d3.format(tickFormat) : tickFormat;

      x = d3.scaleLinear()
        .domain([-1, color.range().length - 1])
        .rangeRound([marginLeft, width - marginRight]);

      svg.append("g")
        .selectAll("rect")
        .data(color.range())
        .join("rect")
        .attr("x", (d: any, i: number) => x(i - 1))
        .attr("y", marginTop)
        .attr("width", (d: any, i: number) => x(i) - x(i - 1))
        .attr("height", height - marginTop - marginBottom)
        .attr("fill", (d: any) => d);

      tickValues = d3.range(thresholds.length);
      tickFormat = (i: any) => thresholdFormat(thresholds[i], i);
    }

    svg.append("g")
      .attr("transform", `translate(0, ${height - marginBottom})`)
      .call(d3.axisBottom(x)
        .ticks(ticks, typeof tickFormat === "string" ? tickFormat : undefined)
        .tickFormat(typeof tickFormat === "function" ? tickFormat : undefined)
        .tickSize(tickSize)
        .tickValues(tickValues))
      .call(g => g.selectAll(".tick line").attr("y1", marginTop + marginBottom - height))
      .call(g => g.select(".domain").remove())
      .call(g => g.append("text")
        .attr("x", marginLeft)
        .attr("y", marginTop + marginBottom - height - 6)
        .attr("fill", "currentColor")
        .attr("text-anchor", "start")
        .attr("padding-bottom", "5px")
        .attr("font-size", 12)
        .text(title));

    return svg.node();
  }

  heatmapChart(data: any): void {
    let me = this;

    // data has already been ordered by dateIndex
    let dates: any[] = [];
    let i=1;
    let hours = ['00','01','02','03','04','05','06','07','08','09','10',
      '11','12','13','14','15','16','17','18','19','20','21','22','23'];
    let fullSet = [];
    let max = 0.0;
    data.forEach((item: any) => {
      let days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
      let str = days[(new Date(item.DATE)).getDay()] + ' ' + item.DATE;
      if(!dates.includes(str)) {
        dates.push(str);
      }
      if(max < item[this.valueType]*1.0) max = item[this.valueType]*1.0;
    });

    if(this.fillAllCells) {
      for (let j = 0; j < dates.length; j++) {
        for (let i = 0; i < hours.length; i++) {
          fullSet.push({
            DATE: dates[j],
            HOUR: hours[i]+':00',
            DATEINDEX: j + 1,
            HOURINDEX: i + 1,
            PAX: 0
          })
        }
      }
      data.forEach((item: any) => {
        let i = (item.DATEINDEX - 1) * hours.length + (item.HOURINDEX - 1);
        fullSet[i] = item;
      });
    } else {
      fullSet = data;
    }

    let margin = { top: 30, right: 10, bottom: 20, left: 120 },
      gridSize = 25,
      width = gridSize * hours.length + 140,
      height = gridSize * dates.length + 60;
    me.figureHeight = height.toString() + 'px';
     max = Math.ceil(max/100) * 100;
    //let colors = d3.scaleSequential(t => d3.interpolateYlGn(t)).domain([0, max]);
    let colors = d3.scaleSequential(t => d3.interpolateBlues(t)).domain([0, max]);

    this.legend('legend', d3.scaleSequential([0, max], d3.interpolateBlues), "Legend");

    this.svg = d3.select("figure#matrix")
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    let dateLabels = this.svg.selectAll(".dateLabel")
      .data(dates)
      .enter().append("text")
      .text((d: any) => d)
      .attr("x", 0)
      .attr("y", (d: any, i: number) => i * gridSize)
      .style("text-anchor", "end")
      .attr("transform", "translate(-6," + gridSize / 1.5 + ")")
      .attr("class", "dateLabel mono axis");

    let hourLabels = this.svg.selectAll(".hourLabel")
      .data(hours)
      .enter().append("text")
      .text((d: any) => d)
      .attr("x", (d: any, i: number) => i * gridSize)
      .attr("y", 0)
      .style("text-anchor", "middle")
      .attr("transform", "translate(" + gridSize / 2 + ", -6)")
      .attr("class", "hourLabel mono axis");


    let cards = this.svg.selectAll(".hour")
      .data(fullSet, (d: any) => d.DATE+'-'+d.HOUR);

    cards.append("title");

    cards.enter().append("rect")
      .attr("x", (d: any) => (d.HOURINDEX - 1) * gridSize)
      .attr("y", (d: any) => (d.DATEINDEX - 1) * gridSize)
      .attr("rx", 4)
      .attr("ry", 4)
      .attr("class", "bordered")
      .attr("width", gridSize)
      .attr("height", gridSize)
      .style("fill", '#fff')
      .on("mouseover", mouseover)
      .on("mouseout", mouseout)
      .merge(cards)
      .transition()
      .duration(1000)
      .style("fill", (d: any) => colors(d[this.valueType]));

    let tooltip = d3.select('figure#matrix').append("div")
      .attr("class", "x-tip x-tip-default x-border-box")
      .style("padding", "5px 10px")
      .style("display", "none")
    ;

    function mouseover(e: any, d: any) {
      tooltip.html("<b>" + formatNumber(d[me.valueType], 'en-US', '1.0-0') + "</b> pax at " + d.DATE + " " + d.HOUR);
      tooltip.style("top", ((d.DATEINDEX*1+1) * gridSize + 300).toString() + "px");
      tooltip.style("left", ((d.HOURINDEX*1+1) * gridSize + 182).toString() + "px");
      tooltip.style("display", "block");
    }

    function mouseout() {
      tooltip.style("display", "none");
    }

    cards.select("title").text((d: any) => d[this.valueType]);

    cards.exit().remove();

  };

  showD3(params: string, reload: boolean): void {
    d3.select("figure#matrix").selectAll("*").remove();

    // Request server once only
    if(!reload && this.data && this.data.length > 0) {
      this.heatmapChart(this.data);
    } else {
      let me = this;
      this.loading = true;
      d3.json(this.globals.url_root + "/Query.action?sql=SQL_HOURLY_TRAFFIC"+params, {
        headers: new Headers({
          "Authorization": "Bearer " + this.token
        })
      }).then(
        (data: any ) => {
          this.loading = false;
          if(data.success) {
            me.data = data.data;
            me.heatmapChart(data.data);
          } else {
            if(data.message) me.messageService.add({
              severity:'warn',
              summary:'Warning',
              detail: data.message
            });
          }
        }
      );
    }
  }

}
